import React, { useMemo, useState } from "react";
import { Alert, Button, Card, Code, Group, Stack, Text, Textarea, Title } from "@mantine/core";
import { ZHelloPayload } from "@multiconsult-group/multiplacer-core";
import {
  usePostSharedZodPingMutation,
} from "../openapi";

const DEFAULT_JSON = JSON.stringify({ message: "hei", count: 1 }, null, 2);

export const SharedTestPage: React.FC = () => {
  const [jsonText, setJsonText] = useState(DEFAULT_JSON);

  const [postSharedZodPing, postState] = usePostSharedZodPingMutation();
  const [postFunctionsSharedZodPing, functionsState] = usePostSharedZodPingMutation();

  const parsedJson = useMemo(() => {
    try {
      return { ok: true as const, value: JSON.parse(jsonText) as unknown };
    } catch (e: any) {
      return { ok: false as const, error: e?.message ?? "Ogiltig JSON" };
    }
  }, [jsonText]);

  const localParse = useMemo(() => {
    if (!parsedJson.ok) return null;
    return ZHelloPayload.safeParse(parsedJson.value);
  }, [parsedJson]);

  const handleBackendTest = async () => {
    if (!parsedJson.ok) return;

    await postSharedZodPing({
      body: parsedJson.value as any,
    }).unwrap();
  };

  const handleFunctionsTest = async () => {
    if (!parsedJson.ok) return;

    await postFunctionsSharedZodPing({
      body: parsedJson.value as any,
    }).unwrap();
  };

  return (
    <Stack p="md" gap="md">
      <Title order={2}>Shared test</Title>

      <Card withBorder>
        <Stack gap="xs">
          <Text fw={600}>Input (JSON)</Text>
          <Textarea
            minRows={6}
            value={jsonText}
            onChange={(e) => setJsonText(e.currentTarget.value)}
            spellCheck={false}
            styles={{ input: { fontFamily: "monospace" } }}
          />

          {!parsedJson.ok && (
            <Alert color="red" title="JSON-fel">
              {parsedJson.error}
            </Alert>
          )}
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="sm">
          <Group justify="space-between">
            <Text fw={600}>1) Lokal Zod-parse</Text>
          </Group>

          {parsedJson.ok && localParse && localParse.success && (
            <Alert color="green" title="OK">
              <Code block>{JSON.stringify(localParse.data, null, 2)}</Code>
            </Alert>
          )}

          {parsedJson.ok && localParse && !localParse.success && (
            <Alert color="red" title="Zod-fel">
              <Code block>{JSON.stringify(localParse.error.flatten(), null, 2)}</Code>
            </Alert>
          )}
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="sm">
          <Group justify="space-between">
            <Text fw={600}>2) Backend (/shared/zod-ping)</Text>
            <Button
              onClick={handleBackendTest}
              disabled={!parsedJson.ok}
              loading={postState.isLoading}
            >
              Testa backend
            </Button>
          </Group>

          {postState.isSuccess && (
            <Alert color="green" title="Svar">
              <Code block>{JSON.stringify(postState.data, null, 2)}</Code>
            </Alert>
          )}

          {postState.isError && (
            <Alert color="red" title="Backend-fel">
              <Code block>{JSON.stringify(postState.error, null, 2)}</Code>
            </Alert>
          )}

          <Text size="sm" c="dimmed">
            Skickar body direkt som JSON. Endpointen validerar med samma schema från
            <Code> @multiconsult-group/multiplacer-core</Code>.
          </Text>
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="sm">
          <Group justify="space-between">
            <Text fw={600}>3) Functions (via backend tRPC: /functions/shared/zod-ping)</Text>
            <Button
              onClick={handleFunctionsTest}
              disabled={!parsedJson.ok}
              loading={functionsState.isLoading}
            >
              Testa functions
            </Button>
          </Group>

          {functionsState.isSuccess && (
            <Alert color="green" title="Svar">
              <Code block>{JSON.stringify(functionsState.data, null, 2)}</Code>
            </Alert>
          )}

          {functionsState.isError && (
            <Alert color="red" title="Functions-fel">
              <Code block>{JSON.stringify(functionsState.error, null, 2)}</Code>
            </Alert>
          )}

          <Text size="sm" c="dimmed">
            Den här går via backend som proxy till Azure Functions, så du kan testa prod-konfig
            (base url / keys) utan att frontend behöver känna till dem.
          </Text>
        </Stack>
      </Card>
    </Stack>
  );
};
